import { useMemo, useCallback } from 'react';
import { EventLabelOption } from '@mark43/cad-api';
import {
    TagGroup,
    Caution,
    HStack,
    VStack,
    Text,
    Popover,
    PopoverTrigger,
    PopoverContent,
} from '@cad-arc';
import { locationCautionPriorityToLevel } from '../common/helpers/styles';
import { CautionLevelAttrId } from '../socket/stream-types';

export function EventLabelsView({
    labels = [],
    truncateCount,
}: {
    labels: EventLabelOption[];
    truncateCount?: number;
}) {
    const { displayedLabels, truncateLabels } = useMemo(
        function getLabels() {
            const sortedLabels = labels.sort((currentLabel, nextLabel) => {
                const currentLabelPriorityLevel = locationCautionPriorityToLevel(
                    currentLabel.locationCautionPriorityGlobalAttrId as CautionLevelAttrId
                );

                const nextLabelPriorityLevel = locationCautionPriorityToLevel(
                    nextLabel.locationCautionPriorityGlobalAttrId as CautionLevelAttrId
                );

                return currentLabelPriorityLevel - nextLabelPriorityLevel;
            });

            if (!truncateCount) {
                return { displayedLabels: sortedLabels };
            }

            const displayedLabels: EventLabelOption[] = [];
            const truncateLabels: EventLabelOption[] = [];

            for (let index = 0; index < sortedLabels.length; index++) {
                const label = sortedLabels[index];

                if (index < truncateCount) {
                    displayedLabels.push(label);
                } else {
                    truncateLabels.push(label);
                }
            }

            return { displayedLabels, truncateLabels };
        },
        [labels, truncateCount]
    );

    const getLabelInDisplayedFormat = useCallback((label: EventLabelOption) => {
        const priority = locationCautionPriorityToLevel(
            label.locationCautionPriorityGlobalAttrId as CautionLevelAttrId
        );

        return (
            <Caution key={label.value} data-priority={priority} priority={priority}>
                {label.label}
            </Caution>
        );
    }, []);

    if (!truncateLabels?.length) {
        return (
            <TagGroup spacing={2}>
                {displayedLabels.map((label) => getLabelInDisplayedFormat(label))}
            </TagGroup>
        );
    }

    return (
        <HStack alignItems="center" gap={1}>
            <HStack alignItems="flex-start">
                {displayedLabels.map((label) => getLabelInDisplayedFormat(label))}
            </HStack>
            <Popover>
                <PopoverTrigger onClick={(event) => event.stopPropagation()}>
                    <Text color="brand" style={{ cursor: 'pointer', textDecoration: 'underline' }}>
                        +{truncateLabels.length}
                    </Text>
                </PopoverTrigger>
                <PopoverContent align="end">
                    <VStack alignItems="center" gap={1}>
                        {truncateLabels.map((label) => getLabelInDisplayedFormat(label))}
                    </VStack>
                </PopoverContent>
            </Popover>
        </HStack>
    );
}
